package one.healthium.insights.ui.personal_mealplan

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import loco.MEAL_PLAN_LOG_FOOD_BUTTON
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import one.healthium.base.utils.Formatters.formatCalories
import one.healthium.insights.domain.models.IngredientModel
import one.healthium.loco.resolve
import one.healthium.insights.domain.models.NutritionModel
import one.healthium.insights.domain.models.RecipeModel
import one.healthium.ui.compose.components.AppButton
import one.healthium.ui.compose.components.AppButtonDefaults
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
internal fun MealSection(
    modifier: Modifier = Modifier,
    label: String,
    recipes: List<RecipeModel>,
    onRecipeClicked: (RecipeModel, String) -> Unit,
    onLogMeal: (RecipeModel) -> Unit,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            text = label,
            style = AppTheme.textStyles.secondary_16_700,
            color = AppTheme.colors.text100,
        )
        recipes.fastForEach { recipe ->
            RecipeItem(
                recipe = recipe,
                mealType = label,
                onClick = { onRecipeClicked(recipe, label) },
                onLogMeal = { onLogMeal(recipe) },
            )
        }
    }
}

@Composable
internal fun RecipeItem(
    modifier: Modifier = Modifier,
    recipe: RecipeModel,
    mealType: String,
    onClick: () -> Unit,
    onLogMeal: () -> Unit,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AppTheme.colors.windowBackground)
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Recipe info
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = "${recipe.emoji} ${recipe.title}",
                style = AppTheme.textStyles.secondary_16_500,
                color = AppTheme.colors.text100,
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = recipe.caloriesPerServing.toFloat().formatCalories().resolve(),
                style = AppTheme.textStyles.secondary_12_400,
                color = AppTheme.colors.text300,
            )
        }

        Spacer(modifier = Modifier.width(8.dp))

        // Log button
        AppButton(
            minHeight = 32.dp,
            contentPadding = PaddingValues(horizontal = 12.dp),
            colors = AppButtonDefaults.appButtonColors(
                contentColor = AppTheme.colors.secondary,
                containerColor = AppTheme.colors.surface,
            ),
            onClick = onLogMeal,
        ) {
            Text(
                text = stringResource(loco.Res.string.MEAL_PLAN_LOG_FOOD_BUTTON),
                style = AppTheme.textStyles.secondary_14_500,
            )
        }

        Spacer(modifier = Modifier.width(4.dp))

        // Chevron — indicates tappable
        Icon(
            modifier = Modifier.size(16.dp),
            painter = painterResource(AppIcons.icArrowRight),
            tint = AppTheme.colors.text300,
            contentDescription = null,
        )
    }
}

// --- Previews ---

private fun previewRecipe(
    title: String = "Garlic Chili Scrambled Eggs",
    emoji: String = "\uD83C\uDF73",
) = RecipeModel(
    id = "preview",
    title = title,
    emoji = emoji,
    servingWeightGrams = 228,
    ingredients = listOf(
        IngredientModel(name = "eggs", emoji = "\uD83E\uDD5A", amount = 4.0, unit = "pcs"),
        IngredientModel(name = "butter", emoji = "\uD83E\uDDC8", amount = 1.0, unit = "tbsp"),
    ),
    steps = listOf("Step 1", "Step 2"),
    nutritionPer100g = NutritionModel(calories = 185.0, protein = 11.7, fat = 14.6, carbs = 2.1),
)

@Preview(showBackground = true)
@Composable
private fun MealSectionPreview() {
    AppTheme {
        MealSection(
            label = "Breakfast",
            recipes = listOf(
                previewRecipe(),
                previewRecipe(title = "Beef Patties", emoji = "\uD83E\uDD69"),
            ),
            onRecipeClicked = { _, _ -> },
            onLogMeal = {},
        )
    }
}

@Preview(showBackground = true)
@Composable
private fun RecipeItemPreview() {
    AppTheme {
        RecipeItem(
            recipe = previewRecipe(),
            mealType = "Breakfast",
            onClick = {},
            onLogMeal = {},
        )
    }
}
